'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { CATEGORY_CARD_IMAGE_SIZES, resolveColorSwatch } from './category-visuals';
import type { ActiveFilters, FilterCategories, FilterKey } from './filter-types';
import { FILTER_KEYS } from './filter-types';

interface BrowseByNavProps {
  categories: FilterCategories;
  activeFilters: ActiveFilters;
  onToggle: (key: FilterKey, handle: string) => void;
  onViewAll: (key: FilterKey) => void;
}

// Matches CATEGORY_CARD_DIMENSIONS — kept as literal Tailwind classes since
// arbitrary-value utilities can't be composed from the shared constant.
const CARD_BOX_CLASSES = 'h-[69px] w-[92px] lg:h-[90px] lg:w-[120px]';

/**
 * Compact browse-by discovery strip. Tabs across the four taxonomy
 * dimensions with a single horizontally scrollable row for the active tab —
 * visual cards (4:3, object-cover) for Subjects/Styles/Rooms, color swatches
 * for Colors. Sized to keep the whole section (tabs + row) under ~125px on
 * mobile so it never pushes the artwork grid out of the initial viewport.
 */
export function BrowseByNav({ categories, activeFilters, onToggle, onViewAll }: BrowseByNavProps) {
  const t = useTranslations('shop.browseBy');
  const [activeTab, setActiveTab] = useState<FilterKey>('subjects');
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const tabLabels: Record<FilterKey, string> = {
    subjects: t('tabs.subjects'),
    styles: t('tabs.styles'),
    rooms: t('tabs.rooms'),
    colors: t('tabs.colors'),
  };

  const availableTabs = FILTER_KEYS.filter(key => categories[key]?.length > 0);
  const options = categories[activeTab] ?? [];
  const selected = new Set(activeFilters[activeTab] ?? []);
  const isColors = activeTab === 'colors';

  const updateScrollState = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    // Re-measure whenever the active tab's content changes.
    updateScrollState();
    el.scrollTo({ left: 0 });

    el.addEventListener('scroll', updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [activeTab, updateScrollState]);

  const scrollByAmount = (direction: 1 | -1) => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <section aria-label={t('sectionLabel')} className="mt-3">
      {/* Tabs */}
      <div
        role="tablist"
        aria-label={t('sectionLabel')}
        className="flex min-h-11 items-center gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1"
      >
        {availableTabs.map(key => {
          const isActive = key === activeTab;
          const count = activeFilters[key]?.length ?? 0;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(key)}
              className={`relative flex min-h-11 shrink-0 items-center gap-1.5 px-3 text-sm font-semibold transition-colors ${
                isActive
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tabLabels[key]}
              {count > 0 && (
                <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {count}
                </span>
              )}
              {isActive && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-amber-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Category row */}
      <div className="relative border-t border-gray-200 pt-2 dark:border-white/10">
        {/* Desktop prev/next — compact, only shown when content overflows */}
        {canScrollPrev && (
          <button
            type="button"
            onClick={() => scrollByAmount(-1)}
            aria-label={t('scrollPrev')}
            className="absolute left-0 top-1/2 z-10 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm hover:text-amber-600 dark:border-white/10 dark:bg-slate-900/95 dark:text-gray-300 lg:flex"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        {canScrollNext && (
          <button
            type="button"
            onClick={() => scrollByAmount(1)}
            aria-label={t('scrollNext')}
            className="absolute right-0 top-1/2 z-10 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm hover:text-amber-600 dark:border-white/10 dark:bg-slate-900/95 dark:text-gray-300 lg:flex"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        <div
          ref={rowRef}
          className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-0.5 -mx-4 px-4 sm:mx-0 sm:px-0 lg:flex-nowrap lg:px-0.5"
        >
          {isColors
            ? options.map(option => {
                const isSelected = selected.has(option.handle);
                return (
                  <button
                    key={option.handle}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggle('colors', option.handle)}
                    className="flex w-16 shrink-0 snap-start flex-col items-center gap-1 text-center"
                  >
                    <span
                      className={`relative flex h-11 w-11 items-center justify-center rounded-full ring-2 ring-offset-2 ring-offset-gray-50 transition-all dark:ring-offset-slate-950 lg:h-12 lg:w-12 ${
                        isSelected ? 'ring-amber-500' : 'ring-transparent'
                      }`}
                      style={{ background: resolveColorSwatch(option.handle, option.name) }}
                    >
                      {isSelected && (
                        <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm ring-2 ring-white dark:ring-slate-950">
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </span>
                      )}
                    </span>
                    <span className="line-clamp-1 w-full text-[11px] font-medium leading-tight text-gray-700 dark:text-gray-300">
                      {option.name}
                    </span>
                  </button>
                );
              })
            : options.map(option => {
                const isSelected = selected.has(option.handle);
                return (
                  <button
                    key={option.handle}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggle(activeTab, option.handle)}
                    className={`relative shrink-0 snap-start overflow-hidden rounded-lg border-2 transition-colors ${CARD_BOX_CLASSES} ${
                      isSelected
                        ? 'border-amber-500 ring-2 ring-amber-500/30'
                        : 'border-transparent hover:border-amber-300 dark:hover:border-amber-500/40'
                    }`}
                  >
                    <Image
                      src={option.imageUrl}
                      alt=""
                      fill
                      sizes={CATEGORY_CARD_IMAGE_SIZES}
                      className="object-cover"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                    {isSelected && (
                      <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm">
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                    )}
                    <span className="absolute inset-x-1 bottom-1 line-clamp-1 text-left text-[11px] font-semibold leading-tight text-white drop-shadow">
                      {option.name}
                    </span>
                  </button>
                );
              })}

          {/* View all */}
          <button
            type="button"
            onClick={() => onViewAll(activeTab)}
            className={`flex shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 transition-colors hover:border-amber-400 hover:text-amber-600 dark:border-white/15 dark:text-gray-300 dark:hover:border-amber-500/50 dark:hover:text-amber-400 ${CARD_BOX_CLASSES}`}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="text-[11px] font-semibold">{t('viewAll')}</span>
          </button>
        </div>
      </div>
    </section>
  );
}
